// Bare specifier resolves to the .mjs build (package.json "module" field),
// which webpack's default 'js' rule (test: /\.m?jsx?$/) picks up — unlike
// the .cjs build, which matches no loader rule at all in this project's
// webpack config. transpileDependencies below routes it through babel-loader
// so its modern syntax (optional chaining) gets down-leveled.
import { driver } from 'driver.js'
import 'driver.js/dist/driver.css'

const STORAGE_PREFIX = 'zc-tour-seen-'

// Pages that offer a guided tour, keyed by an id used both for the
// localStorage "already seen" flag and for Header.vue to know when to show
// the replay ("?") button. Matched against the current route by path since
// a couple of routes in this app reuse the same route `name` for both a
// list and a form (e.g. "Customers"), which name-based matching would
// wrongly treat as the same page.
export const TOUR_PAGES = [
  { key: 'areas-list', test: (route) => route.path === '/areas-list' },
  { key: 'area-item', test: (route) => /^\/areas\/[^/]+$/.test(route.path) },
  { key: 'tree-types', test: (route) => route.path === '/tree-types' },
  { key: 'customers', test: (route) => route.path === '/customers' },
  { key: 'retire-tokens', test: (route) => route.path === '/retire-tokens' }
]

export function currentTourKey(route) {
  const page = TOUR_PAGES.find((p) => p.test(route))
  return page ? page.key : null
}

export function hasSeenTour(key) {
  try {
    return window.localStorage.getItem(STORAGE_PREFIX + key) === '1'
  } catch (e) {
    return false
  }
}

function markTourSeen(key) {
  try {
    window.localStorage.setItem(STORAGE_PREFIX + key, '1')
  } catch (e) {
    // Storage unavailable (private mode, quota, etc.) — the tour will just
    // show again next visit, which is a harmless fallback.
  }
}

// Steps whose target element doesn't exist yet (admin-only buttons, rows
// that haven't loaded) are dropped instead of breaking the whole tour —
// `driver.js`'s own `skipMissingElement` only skips at run time and still
// logs/breaks positioning, so filtering up front is more reliable.
function resolveSteps(steps) {
  return steps.filter((step) => {
    if (!step.element) return true
    try {
      return !!document.querySelector(step.element)
    } catch (e) {
      return false
    }
  })
}

let activeDriver = null

export function runTour(key, steps) {
  if (activeDriver) {
    activeDriver.destroy()
    activeDriver = null
  }

  const resolved = resolveSteps(steps)
  if (!resolved.length) return null

  activeDriver = driver({
    animate: true,
    smoothScroll: true,
    allowClose: true,
    overlayColor: 'rgba(6, 14, 9, 0.72)',
    stagePadding: 6,
    stageRadius: 12,
    popoverClass: 'zc-tour-popover',
    showProgress: true,
    progressText: '{{current}} / {{total}}',
    nextBtnText: 'Next',
    prevBtnText: 'Back',
    doneBtnText: 'Done',
    showButtons: ['next', 'previous', 'close'],
    steps: resolved,
    onPopoverRender: (popover) => {
      // The library's close (X) button becomes our "Skip" action — same
      // spot a user expects a dismiss control, just labeled explicitly.
      popover.closeButton.textContent = 'Skip'
      popover.closeButton.classList.add('zc-tour-skip-btn')
    },
    onDestroyed: () => {
      markTourSeen(key)
      activeDriver = null
    }
  })

  activeDriver.drive()
  return activeDriver
}

export function maybeAutoRunTour(key, steps) {
  if (hasSeenTour(key)) return null
  return runTour(key, steps)
}
